import { AlertIcon } from '@approvalmax/ui';
import { Alert, Button, Checkbox, Flex, Form, Popup, Spacing, Text, TextField } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { memo, ReactNode } from 'react';
import bemFactory from 'react-bem-factory';

import { usePopupDeleteOrganisationData } from './PopupDeleteOrganisation.hooks';
import { messages } from './PopupDeleteOrganisation.messages';
import { popupDeleteOrganisationState } from './PopupDeleteOrganisation.states';
import { PopupDeleteOrganisationProps } from './PopupDeleteOrganisation.types';

const qa = bemFactory.qa('cps-delete-company-popup');

export const PopupDeleteOrganisation = memo<PopupDeleteOrganisationProps>((props) => {
    const { company } = props;

    const [isOpenDeletePopup, setIsOpenDeletePopup] = useAtom(popupDeleteOrganisationState);
    const { form, onFormSubmit, isLoading, canSubmit } = usePopupDeleteOrganisationData(company);

    return (
        <Popup open={isOpenDeletePopup} onToggle={setIsOpenDeletePopup} size='small' data-qa={qa()}>
            <Popup.Header title={messages.title} progress={isLoading} />

            <Popup.Body>
                <Form form={form} onSubmit={onFormSubmit}>
                    <Flex direction='column' spacing='24'>
                        <Text font='label' fontSize='medium'>
                            {messages.description({
                                companyName: <b>{company.displayName}</b>,
                            })}
                        </Text>

                        {company.flags.isPaid && (
                            <div>
                                <Alert
                                    startIcon={<AlertIcon size={24} />}
                                    size='small'
                                    invalid={true}
                                    color='midnight20'
                                    alignItems='center'
                                >
                                    {messages.extendedDescription({ b: (chunks: ReactNode[]) => <b>{chunks}</b> })}
                                </Alert>

                                <Spacing height={24} />

                                <Checkbox name='agreed' required={company.flags.isPaid}>
                                    {messages.confirmCheckboxText}
                                </Checkbox>
                            </div>
                        )}

                        <TextField
                            name='confirmation'
                            size='medium'
                            label={messages.inputLabel}
                            initFocus
                            placeholder={messages.inputPlaceholderText}
                            data-qa={qa('confirmation-input')}
                            required
                        />

                        <Flex justifyContent='end'>
                            <Button
                                type='submit'
                                size='medium'
                                color='red40'
                                data-qa={qa('delete-button')}
                                disabled={!canSubmit}
                            >
                                {messages.delete}
                            </Button>
                        </Flex>
                    </Flex>
                </Form>
            </Popup.Body>
        </Popup>
    );
});

PopupDeleteOrganisation.displayName = 'PopupDeleteOrganisation';
